<template>
  <div>
    <b-loading is-full-page :active="loading.general" can-cancel></b-loading>

    <form>
      <b-notification
        v-if="auction.tipo === 'DISPENSA'"
        type="is-info"
        :closable="false"
        class="mb-3"
      >
        <b-icon icon="information-outline" size="is-small" class="mr-1" />
        <strong>Atenção:</strong>
        Todos os documentos estarão disponíveis para análise na etapa de habilitação.
      </b-notification>
      <b-field grouped class="is-flex-touch column-direction-touch">
        <b-field
          expanded
          label="Documento"
          :class="{ 'custom-document-select': customDocument }"
          :type="campoInvalido('type', 'data') ? 'is-danger' : ''"
          :message="campoInvalido('type', 'data') ? 'Documento é um campo obrigatório' : ''"
        >
          <b-select
            :disabled="disableFields()"
            expanded
            placeholder="Selecione um documento requerido"
            v-model="$v.data.type.$model"
            @click.native="$v.data.type.$touch()"
            @input="onDocumentSelect"
          >
            <option
              v-for="documentType in filteredDocumentTypes"
              :key="documentType.id"
              :value="documentType.type"
            >
              {{ documentType.type }}
            </option>
          </b-select>
        </b-field>

        <b-field
          expanded
          label="Nome"
          v-if="customDocument"
          :class="{ 'custom-input-name': customDocument }"
          :type="campoInvalido('name', 'data') ? 'is-danger' : ''"
          :message="campoInvalido('name', 'data') ? 'Nome é um campo obrigatório' : ''"
        >
          <b-input
            :disabled="disableFields()"
            expanded
            custom-class="uppercase"
            maxlength="235"
            ref="name"
            placeholder="Insira um nome"
            v-model="$v.data.name.$model"
            @input="value => converterMaiusculo('name', value, 'data')"
            @blur="event => converterMaiusculo('name', event.target.value, 'data')"
          ></b-input>
        </b-field>
      </b-field>

      <b-field grouped class="is-flex-touch column-direction-touch etapa-row">
        <b-field
          expanded
          class="is-flex is-align-items-center"
          :type="campoInvalido('stage', 'data') ? 'is-danger' : ''"
          :message="campoInvalido('stage', 'data') ? 'Etapa é um campo obrigatório' : ''"
        >
          <template #label>
            <span>
              Documento será exigido na etapa
              <b-tooltip
                label="Selecione a etapa onde o licitante deverá enviar esse documento"
                position="is-bottom"
                multilined
              >
                <b-icon
                  icon="help-circle"
                  size="is-small"
                  class="has-text-grey is-clickable"
                  style="margin-left: 4px"
                />
              </b-tooltip>
              :
            </span>
          </template>

          <span style="margin-right: 10px"></span>

          <b-radio-button
            v-for="option in availableStageOptions"
            v-model="data.stage"
            :key="option.value"
            :native-value="option.value"
            :disabled="disableFields()"
          >
            {{ option.label }}
          </b-radio-button>
        </b-field>

        <b-field class="custom-save-button">
          <button
            class="button is-primary is-fullwidth adicionar"
            :disabled="disableFields()"
            @click.prevent.stop="saveRequiredDocument()"
          >
            Salvar
          </button>
        </b-field>
      </b-field>
    </form>

    <hr />

    <AppPaginatedTable v-if="requiredDocuments.length" :data="requiredDocuments">
      <b-table-column label="Documento Requerido" v-slot="props">
        {{ props.row.type }}
      </b-table-column>

      <b-table-column label="Etapa" v-slot="props">
        <b-tag type="is-warning">
          {{ stageLabel(props.row) }}
        </b-tag>
      </b-table-column>

      <b-table-column centered label="Opções" width="120" v-slot="props">
        <b-tooltip label="Excluir" position="is-right">
          <button
            class="button is-danger"
            :disabled="disableFields() || disable.delete"
            @click.prevent.stop="deleteRequiredDocument(props.row.id)"
          >
            <b-icon icon="delete" />
          </button>
        </b-tooltip>
      </b-table-column>
    </AppPaginatedTable>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
import campo from '@/mixins/formulario';
import pregaoEditarFields from '@/mixins/pregaoEditarFields';
import AppPaginatedTable from '@/components/AppPaginatedTable';

export default {
  name: 'RequiredDocuments',
  components: {
    AppPaginatedTable,
  },
  mixins: [campo, pregaoEditarFields],
  data() {
    const data = {
      type: null,
      name: null,
      stage: null,
    };
    const disable = {
      delete: false,
    };
    const loading = {
      general: false,
    };

    const stageOptions = [
      { value: 'propostas', label: 'Etapa de Propostas' },
      { value: 'habilitacao', label: 'Etapa de Habilitação' },
    ];

    return {
      data,
      disable,
      loading,
      stageOptions,
    };
  },
  validations: {
    data: {
      type: { required },
      name: {
        required: requiredIf(function validateCustomDocument() {
          return this.customDocument;
        }),
      },
      stage: { required },
    },
  },
  computed: {
    ...mapState('documentTypes', ['documentTypes']),
    ...mapState('requiredDocuments', ['requiredDocuments']),
    ...mapGetters({
      auction: 'obterOfertaCompra',
    }),
    isPhaseInversionEnabled() {
      return !!this.auction?.phase_inversion;
    },
    availableStageOptions() {
      if (this.isPhaseInversionEnabled) {
        return this.stageOptions.filter(option => option.value === 'propostas');
      }

      return this.stageOptions;
    },
    customDocument() {
      return ['Declarações', 'Outros Documentos'].indexOf(this.data.type) > -1;
    },
    filteredDocumentTypes() {
      return this.documentTypes
        .filter(documentType => documentType.required)
        .reduce((documents, document) => {
          if (!document.unique) {
            documents.push(document);
          } else {
            const exists = this.requiredDocuments.find(element => element.type === document.type);
            if (!exists) documents.push(document);
          }
          return documents;
        }, [])
        .sort((a, b) => a.type.localeCompare(b.type));
    },
  },
  methods: {
    ...mapActions('documentTypes', ['fetchDocumentTypes']),
    ...mapActions('requiredDocuments', [
      'addRequiredDocument',
      'fetchRequiredDocuments',
      'removeRequiredDocument',
    ]),
    ...mapActions([
      'alternarEmPublicacaoPregao',
      'atualizarOfertaCompraEdital',
      'buscarOfertaCompra',
    ]),
    onDocumentSelect(type) {
      this.selectInputName(type);

      if (!type) {
        this.data.stage = null;
        return;
      }

      if (this.isPhaseInversionEnabled) {
        this.data.stage = 'propostas';
        return;
      }

      const doc = this.documentTypes.find(d => d.type === type);

      if (!doc) {
        this.data.stage = null;
        return;
      }

      if (doc.proposals) {
        this.data.stage = 'propostas';
      } else if (doc.qualification) {
        this.data.stage = 'habilitacao';
      } else {
        this.data.stage = null;
      }
    },
    stageLabel(row) {
      if (row.propostas) return 'Etapa de Propostas';
      if (row.habilitacao) return 'Etapa de Habilitação';
      return '';
    },
    async deleteRequiredDocument(id) {
      this.disable.delete = true;
      try {
        await this.removeRequiredDocument({ auctionId: this.auction.id, requiredDocumentId: id });
        this.$alerta('Documento requerido removido', 'is-success');
      } catch (error) {
        this.$alerta('Erro ao remover documento requerido', 'is-danger');
      }
      this.disable.delete = false;
    },
    async saveRequiredDocument() {
      if (this.validar('data')) {
        const stage = this.isPhaseInversionEnabled ? 'propostas' : this.data.stage;
        const requiredType = this.customDocument
          ? `${this.data.name} (${this.data.type})`
          : this.data.type;

        this.loading.general = true;
        try {
          await this.addRequiredDocument({
            auctionId: this.auction.id,
            type: requiredType,
            stage,
          });
          this.data.type = null;
          this.data.name = null;
          this.data.stage = null;
          this.$alerta('Documento requerido adicionado', 'is-success');
        } catch (error) {
          this.$alerta('Erro ao adicionar documento requerido', 'is-danger');
        }

        this.$v.data.$reset();
        this.loading.general = false;
      }
    },
    selectInputName(value) {
      if (['Declarações', 'Outros Documentos'].indexOf(value) > -1) {
        setTimeout(() => {
          this.$refs.name.focus();
        }, 100);
      }
    },
  },
  async created() {
    this.loading.general = true;
    try {
      await Promise.all([
        !this.auction.id ? this.buscarOfertaCompra(this.$route.params.id) : Promise.resolve(),
      ]);
      await Promise.all([
        !this.auction.id ? this.buscarOfertaCompra(this.$route.params.id) : Promise.resolve(),
        this.fetchRequiredDocuments(this.$route.params.id),
        this.fetchDocumentTypes(),
      ]);
    } catch (error) {
      this.$alerta('Erro ao buscar dados', 'is-danger');
    } finally {
      this.loading.general = false;
    }
  },
  beforeDestroy() {
    this.alternarEmPublicacaoPregao(false);
  },
};
</script>

<style lang="scss" scoped>
.custom-document-select {
  width: 20%;
}

.custom-input-name {
  width: 70%;
}

.custom-save-button {
  width: 78px;
}

.etapa-row {
  margin-top: 0.5rem;
}
</style>
